/**
 * Lockstep socket client.
 *
 * Two properties matter more than anything else here:
 *
 *  1. **Reconnect is automatic and silent.** A phone that sleeps, a laptop lid,
 *     a wifi handover — none of these should require a person in a classroom to
 *     do anything. The socket backs off and retries, and replays whatever
 *     identifies this client the moment it is open again.
 *
 *  2. **State is replaced, never merged.** Every `session:state` is a full
 *     snapshot, so the client throws away what it had. There is no reconciliation
 *     path to get wrong, which is exactly why a refresh lands in the right place.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientEvent, InstructorState, ParticipantState, ServerEvent } from '@examatron/shared';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 15_000;
const HEARTBEAT_MS = 30_000;

let cachedUrl: string | null = null;

async function socketUrl(): Promise<string> {
  if (cachedUrl) return cachedUrl;
  const res = await fetch('/api/config');
  const body = (await res.json()) as { wsUrl: string | null };
  if (!body.wsUrl) throw new Error('The realtime channel is not configured.');
  cachedUrl = body.wsUrl;
  return cachedUrl;
}

export interface Socket<S> {
  state: S | null;
  connection: ConnectionState;
  /** Last error pushed by the server, cleared on the next successful snapshot. */
  error: string | null;
  send: (event: ClientEvent) => void;
}

/**
 * @param hello  What to send once the socket opens — the join, resume, or attach
 *               that identifies this client. Called on every reconnect, so it
 *               must be safe to repeat. Returning null holds the socket open
 *               without identifying (used while a join form is still being filled).
 */
export function useSocket<S extends ParticipantState | InstructorState>(
  hello: () => ClientEvent | null,
): Socket<S> {
  const [state, setState] = useState<S | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [error, setError] = useState<string | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const attempt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const heartbeat = useRef<ReturnType<typeof setInterval>>(undefined);
  const closed = useRef(false);

  // Held in a ref so a changing closure never forces the socket to reconnect.
  const helloRef = useRef(hello);
  helloRef.current = hello;

  const send = useCallback((event: ClientEvent) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(event));
    }
  }, []);

  useEffect(() => {
    closed.current = false;

    const connect = async () => {
      if (closed.current) return;

      let url: string;
      try {
        url = await socketUrl();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Cannot reach the server.');
        schedule();
        return;
      }

      const sock = new WebSocket(url);
      ws.current = sock;

      sock.onopen = () => {
        if (closed.current) return sock.close();
        attempt.current = 0;
        setConnection('open');

        const greeting = helloRef.current();
        if (greeting) sock.send(JSON.stringify(greeting));

        // API Gateway closes an idle socket at 10 minutes. A question can easily
        // sit on screen longer than that while it is discussed.
        heartbeat.current = setInterval(() => {
          if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: 'ping' }));
        }, HEARTBEAT_MS);
      };

      sock.onmessage = (ev) => {
        const message = JSON.parse(String(ev.data)) as ServerEvent;
        if (message.type === 'session:state') {
          setState(message.state as S);
          setError(null);
          return;
        }
        if (message.type === 'error') {
          setError(message.message);
          return;
        }
        // Every other event is an optimisation over waiting for the next
        // snapshot; the snapshot remains the source of truth.
        setState((prev) => (prev ? applyEvent(prev, message) : prev));
      };

      sock.onclose = () => {
        clearInterval(heartbeat.current);
        if (closed.current) return;
        setConnection('reconnecting');
        schedule();
      };

      sock.onerror = () => sock.close();
    };

    const schedule = () => {
      if (closed.current) return;
      // Exponential backoff with jitter, so thirty phones in one room do not
      // retry in lockstep and hammer the endpoint on the same tick.
      const delay = Math.min(BASE_DELAY_MS * 2 ** attempt.current, MAX_DELAY_MS);
      attempt.current++;
      timer.current = setTimeout(connect, delay * (0.5 + Math.random() * 0.5));
    };

    /*
     * Waking up.
     *
     * A backgrounded tab has its timers throttled and a locked phone has them
     * suspended outright, so the heartbeat stops and API Gateway closes the
     * socket at its 10-minute idle mark. That is the ordinary shape of a
     * ten-minute break with thirty phones in pockets.
     *
     * Relying on `onclose` alone to notice is not enough: the event can arrive
     * late, and a socket can come back reading OPEN while actually being dead at
     * the other end. So on wake we reconnect outright if it is not open, and
     * probe with a ping if it is — a probe against a dead socket fails and
     * routes into the same reconnect path.
     *
     * The backoff counter is reset first, because a wake is new information and
     * the person is looking at the screen right now.
     */
    const revive = () => {
      if (closed.current || document.visibilityState !== 'visible') return;

      const sock = ws.current;
      if (sock?.readyState === WebSocket.OPEN) {
        try {
          sock.send(JSON.stringify({ type: 'ping' }));
        } catch {
          sock.close();
        }
        return;
      }
      if (sock?.readyState === WebSocket.CONNECTING) return;

      clearTimeout(timer.current);
      attempt.current = 0;
      setConnection('reconnecting');
      void connect();
    };

    document.addEventListener('visibilitychange', revive);
    window.addEventListener('online', revive);
    window.addEventListener('pageshow', revive);

    void connect();

    return () => {
      closed.current = true;
      document.removeEventListener('visibilitychange', revive);
      window.removeEventListener('online', revive);
      window.removeEventListener('pageshow', revive);
      clearTimeout(timer.current);
      clearInterval(heartbeat.current);
      ws.current?.close();
      setConnection('closed');
    };
  }, []);

  return { state, connection, error, send };
}

/** Narrow updates that arrive between snapshots. Anything unknown is ignored. */
function applyEvent<S extends ParticipantState | InstructorState>(prev: S, ev: ServerEvent): S {
  switch (ev.type) {
    case 'answer:accepted':
      return prev.role === 'participant' ? { ...prev, myChoice: ev.choice } : prev;
    case 'progress:update':
      return prev.role === 'instructor' ? { ...prev, progress: ev.progress } : prev;
    case 'roster:update':
      return prev.role === 'instructor' ? { ...prev, roster: ev.roster } : prev;
    default:
      return prev;
  }
}
